import { apiOrderFood } from '@/api/OrderFood'
import { convertKeysToCamelCase } from '@/js/utils/bff'
import { getDisplaySpec } from '@/js/utils/orderFood'
import i18n from '@/lang/i18n'

const state = {
  // 類別列表
  menuList: [],
  // 規格列表
  specList: [],
  /**
   * 點數類型列表
   * 1. 數值相應於餐點品項中 prices 的 priceType
   */
  pointTypeList: []
}

const getters = {
  /**
   * 取得餐點類別項目
   * @param {string} id 餐點類別id
   */
  getMenuItem: (state) => (id) => {
    return state.menuList.find(item => item.id === id)
  },
  /**
   * 取得餐點品項
   * @param {string} id 餐點品項id
   */
  getMenuFoodItem: (state) => (id) => {
    return state.menuList.flatMap(menu => menu.children).find(child => child.id === id)
  },
  /**
   * 取得餐點類別列表最新的排序值
   */
  getMenuListLatestSequence: (state) => {
    return state.menuList.length > 0 ? state.menuList[state.menuList.length - 1].sequence : 0
  },
  /**
   * 取得規格項目
   * @param {string} id 規格id
   */
  getSpecItem: (state) => (id) => {
    return state.specList.find(item => item.id === id)
  },
  /**
   * 取得規格列表最新的排序值
   */
  getSpecListLatestSequence: (state) => {
    return state.specList.length > 0 ? state.specList[state.specList.length - 1].sequence : 0
  },
  /**
   * 取得可用的規格列表
   * 1. 用於處理規格選擇
   */
  getAvailableSpecList: (state) => {
    return state.specList.filter(item => item.isCheck === 1)
      .map(({ id, name, sequence, children }) => {
        return {
          id,
          name,
          sequence,
          children: children.filter(child => child.isCheck === 1)
            .map(({ id, name, sequence }) => ({ id, name, sequence }))
        }
      })
  },
  /**
   * 取得點數類型標題
   * 1. 新點餐資料中的點數類型設計
   */
  getPointTypeTitle: (state) => (type) => {
    return state.pointTypeList.find(item => item.value === type)?.title || '-'
  },
  /**
   * 取得點數類型的id
   */
  getPointTypeId: (state) => (type) => {
    return state.pointTypeList.find(item => item.value === type)?.ptId || 0
  }
}

const mutations = {
  setPointTypeList (state, data) {
    state.pointTypeList = []
    data.forEach(item => {
      state.pointTypeList.push({
        ...item,
        title: item.pointName,
        value: item.ptId === 0 ? 1 : 2
      })
    })
  },
  setMenuList (state, data) {
    state.menuList = []
    data.forEach((menuType) => {
      menuType.isExpand = false
      menuType.children.forEach((menuItem) => {
        /**
         * 設定顯示規格
         * Ex => 甜度：正常，冰塊：少冰
         */
        menuItem.displaySpec = getDisplaySpec(menuItem.chooses, ', ')
      })
      state.menuList.push(menuType)
    })
  },
  updateMenuListOrder (state, { oldIndex, newIndex }) {
    const movedItem = state.menuList.splice(oldIndex, 1)[0]
    state.menuList.splice(newIndex, 0, movedItem)
  },
  updateMenuItemExpand (state, { id, status }) {
    const targetItem = state.menuList.find(item => item.id === id)
    if (targetItem) { targetItem.isExpand = status }
  },
  setSpecList (state, data) {
    state.specList = []
    data.forEach((item) => {
      item.isExpand = false
      state.specList.push(item)
    })
  },
  updateSpecListOrder (state, { oldIndex, newIndex }) {
    const movedItem = state.specList.splice(oldIndex, 1)[0]
    state.specList.splice(newIndex, 0, movedItem)
  },
  updateSpecItemExpand (state, { id, status }) {
    const targetItem = state.specList.find(item => item.id === id)
    if (targetItem) { targetItem.isExpand = status }
  }
}

const actions = {
  getPointType ({ commit }) {
    return new Promise((resolve, reject) => {
      apiOrderFood.getPointType()
        .then(res => {
          const { status, data } = convertKeysToCamelCase(res.data)
          if (status === 1) {
            commit('setPointTypeList', data.pointTypes)
            resolve(true)
          } else {
            reject(new Error(i18n.t('OrderFood.GetPointTypeFailed')))
          }
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  // 取得餐點分類
  getFoodMenu ({ commit }) {
    return new Promise((resolve, reject) => {
      apiOrderFood.getFoodMenu()
        .then(res => {
          const { status, data, errorMessage } = convertKeysToCamelCase(res.data)
          if (status === 1) {
            commit('setMenuList', data.tableFoodMenuSettings)
            resolve(true)
          } else {
            reject(errorMessage)
          }
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  // 取得規格列表
  getFoodSpec ({ commit }) {
    return new Promise((resolve, reject) => {
      apiOrderFood.getFoodSpec()
        .then(res => {
          const { status, data, errorMessage } = convertKeysToCamelCase(res.data)
          if (status === 1) {
            commit('setSpecList', data.tableFoodSpecSettings)
            resolve(true)
          } else {
            reject(errorMessage)
          }
        })
        .catch(error => {
          reject(error)
        })
    })
  }
}

export default { state, getters, mutations, actions }
